import axios from 'axios';
import swell from 'swell-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : '';

// Initialize Swell
const STORE_ID = process.env.REACT_APP_SWELL_STORE_ID;
const PUBLIC_KEY = process.env.REACT_APP_SWELL_PUBLIC_KEY;

console.log('[Swell Init] Store ID configured:', !!STORE_ID);
console.log('[Swell Init] Public Key configured:', !!PUBLIC_KEY);
console.log('[Backend] Backend URL:', BACKEND_URL || 'Not configured (backend optional)');

swell.init(STORE_ID, PUBLIC_KEY);

console.log('[Swell Init] Swell initialized');

// Create axios instance (Optional - only if backend URL is configured)
const api = BACKEND_URL ? axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
}) : null;

// Brand Category IDs (from Swell)
const CAT_LOLLIPOP = "69668cffc45e5d00128a5d22";
const CAT_POPSICLE = "69668d61d4e9f500129812a4";

// Default brands (Fallback for when API is unavailable)
export const DEFAULT_BRANDS = [
  {
    name: "Lollipop",
    slug: "lollipop",
    tagline: "Bold • Loud • Unapologetic",
    colors: { primary: "#FFC1CC", secondary: "#98FF98", accent: "#FFF44F" },
    font: "lollipop"
  },
  {
    name: "Popsicle",
    slug: "popsicle",
    tagline: "Bold • Neon • 80s Arcade",
    colors: { primary: "#00FFFF", secondary: "#FF4500", accent: "#32CD32" },
    font: "popsicle"
  }
];

// Brands API
export const brandsAPI = {
  getAll: async () => {
    if (!api) {
      console.log('[Brands] Using default brands (no backend configured)');
      return DEFAULT_BRANDS;
    }
    try {
      const response = await api.get('/brands');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch brands from backend, using defaults:', error);
      return DEFAULT_BRANDS;
    }
  },
  getBySlug: async (slug) => {
    if (!api) {
      return DEFAULT_BRANDS.find(b => b.slug === slug);
    }
    try {
      const response = await api.get(`/brands/${slug}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch brand ${slug} from backend, using default:`, error);
      return DEFAULT_BRANDS.find(b => b.slug === slug);
    }
  }
};

const transformProduct = (p) => {
  // Get brand from product attributes (Swell wraps attributes in objects with metadata)
  const attributes = p.attributes || {};
  const brandAttr = attributes.brand || {};
  // Extract the actual value from the attribute object
  const assigned_brand = (typeof brandAttr === 'object' && brandAttr.value) ? brandAttr.value : (brandAttr || "lollipop");

  // Get material attribute (e.g., "organic cotton")
  const materialAttr = attributes.material || {};
  let material = null;
  if (typeof materialAttr === 'string') {
    material = materialAttr;
  } else if (typeof materialAttr === 'object' && materialAttr !== null) {
    material = materialAttr.value || null;
  }
  // Ensure material is always a string or null, never an object
  if (typeof material === 'object') {
    material = null;
  }

  // Get video attribute
  const videoAttr = attributes.video || {};
  let video = null;
  if (typeof videoAttr === 'string') {
    video = videoAttr;
  } else if (typeof videoAttr === 'object' && videoAttr !== null) {
    video = videoAttr.value || null;
  }
  // Ensure video is always a string or null, never an object
  if (typeof video === 'object') {
    video = null;
  }

  const images = (p.images || []).map(img => img.file?.url);

  // Get category from product (take first non-brand category)
  const categories = p.categories || [];
  let category = "apparel"; // Default fallback
  if (categories.length > 0) {
    // Get the first category name
    const firstCat = categories[0];
    category = (firstCat.name || firstCat).toLowerCase();
  }

  // Get prices from top-level fields (Swell's preferred way for display)
  // For display: use 'price' as list price, 'sale_price' as sale, 'orig_price' as fallback original
  const basePrice = p.price || p.purchase_options?.standard?.price || 0;
  const salePrice = p.sale_price || p.purchase_options?.standard?.sale_price || null;
  const origPrice = p.orig_price || basePrice; // orig_price is the non-sale price
  const onSale = salePrice != null && salePrice > 0 && salePrice < origPrice;

  // Extract variant stock information
  let variants = [];
  let totalStock = 0;
  let sizes = ["S", "M", "L", "XL"]; // Default fallback
  
  // Size order for sorting
  const sizeOrder = { 'XS': 0, 'S': 1, 'M': 2, 'L': 3, 'XL': 4, 'XXL': 5 };
  
  // Extract colors from variants
  let colors = [];
  let colorImages = {}; // Map colors to their images

  if (p.variants && p.variants.results && p.variants.results.length > 0) {
    variants = p.variants.results.map(v => {
      // Extract color and size from variant name
      // Handles formats: "Black S", "Black, S", "White M", "White, M"
      const cleanName = v.name ? v.name.replace(/,/g, '').trim() : '';
      const parts = cleanName.split(/\s+/);
      const size = parts.length > 0 ? parts[parts.length - 1] : cleanName;
      const color = parts.length > 1 ? parts.slice(0, -1).join(' ') : null;

      // Extract variant images - check photo attribute first, then images array
      let variantImages = [];

      // Check for photo attribute (custom Swell attribute)
      if (v.attributes?.photo) {
        const photoAttr = v.attributes.photo;
        const photoUrl = (typeof photoAttr === 'object' && photoAttr.file?.url)
          ? photoAttr.file.url
          : (typeof photoAttr === 'string' ? photoAttr : null);
        if (photoUrl) {
          variantImages.push(photoUrl);
        }
      }

      // Fallback to variant images array
      if (variantImages.length === 0 && v.images) {
        variantImages = v.images.map(img => img.file?.url || img.url).filter(Boolean);
      }

      // Map color to images - prioritize variant-specific images
      if (color && variantImages.length > 0) {
        // If we already have images for this color, append new ones
        if (colorImages[color]) {
          colorImages[color] = [...new Set([...colorImages[color], ...variantImages])];
        } else {
          colorImages[color] = variantImages;
        }
      }

      return {
        id: v.id,
        name: v.name, // Keep original name for reference
        size: size,
        color: color,
        stock: v.stock_level || 0,
        price: v.price || basePrice,
        images: variantImages
      };
    });
    totalStock = variants.reduce((sum, v) => sum + v.stock, 0);

    // Extract unique sizes and colors
    sizes = [...new Set(variants.map(v => v.size))]
      .filter(Boolean)
      .sort((a, b) => {
        const orderA = sizeOrder[a.toUpperCase()] ?? 999;
        const orderB = sizeOrder[b.toUpperCase()] ?? 999;
        return orderA - orderB;
      });

    colors = [...new Set(variants.map(v => v.color).filter(Boolean))].sort();

    // First, set up product-level images as base for all colors (positional grouping)
    if (colors.length > 0 && images.length > 0) {
      const imagesPerColor = Math.floor(images.length / colors.length);

      colors.forEach((color, index) => {
        const startIdx = index * imagesPerColor;
        const endIdx = index === colors.length - 1 ? images.length : startIdx + imagesPerColor;
        const productImages = images.slice(startIdx, endIdx);

        // If variant photos exist for this color, prepend them before product images
        // Otherwise, just use product images
        if (colorImages[color] && colorImages[color].length > 0) {
          colorImages[color] = [...colorImages[color], ...productImages];
        } else {
          colorImages[color] = productImages;
        }
      });
    }
  } else if (p.stock_level !== undefined) {
    // Fallback to product-level stock
    totalStock = p.stock_level || 0;
  }

  // Ensure all string fields are actually strings, not objects
  const safeName = typeof p.name === 'string' ? p.name : (p.name?.toString() || 'Untitled');
  const safeDescription = typeof p.description === 'string' ? p.description : '';
  const safeCategory = typeof category === 'string' ? category : 'apparel';
  const safeBrand = typeof assigned_brand === 'string' ? assigned_brand : 'lollipop';

  return {
    id: p.id,
    slug: p.slug,
    name: safeName,
    description: safeDescription,
    price: basePrice,
    orig_price: onSale ? origPrice : null,
    sale_price: onSale ? salePrice : null,
    on_sale: onSale,
    brand: safeBrand,
    category: safeCategory,
    tags: p.tags || [],
    sizes: sizes,
    colors: colors,
    colorImages: colorImages, // Map of color -> images array
    variants: variants,
    stock_total: totalStock,
    images: images.length > 0 ? images : ["https://placehold.co/400"],
    in_stock: totalStock > 0 || p.active !== false,
    material: material,
    video: video
  };
};

const transformCartItem = async (item) => {
  if (!item) return null;

  // Extract size from options array
  const sizeOption = item.options?.find(opt =>
    opt.name?.toLowerCase() === 'size' || opt.id === 'size'
  );

  // Extract color and size from variant name
  // Handles formats: "Black L", "Black, L", "White S", "White, S"
  let color = null;
  let size = sizeOption?.value || '';

  if (item.variant?.name) {
    // Remove commas and split by whitespace
    const cleanName = item.variant.name.replace(/,/g, '').trim();
    const parts = cleanName.split(/\s+/);
    size = parts.length > 0 ? parts[parts.length - 1] : cleanName;
    color = parts.length > 1 ? parts.slice(0, -1).join(' ') : null;
  }

  // Fetch full product with variants (cart expand doesn't include variants properly)
  let fullProduct = null;
  if (item.product?.id) {
    try {
      const productWithVariants = await swell.products.get(item.product.id, {
        expand: ['variants', 'images']
      });
      fullProduct = productWithVariants ? transformProduct(productWithVariants) : transformProduct(item.product);
    } catch (error) {
      console.error('Failed to fetch full product for cart item:', error);
      fullProduct = transformProduct(item.product);
    }
  } else {
    fullProduct = item.product ? transformProduct(item.product) : null;
  }

  return {
    ...item,
    size: size,
    color: color,
    variantName: item.variant?.name || '', // Keep full variant name for reference
    product: fullProduct
  };
};

// Add auth token to requests (only if backend is configured)
if (api) {
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('pop_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle auth errors
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        const url = error.config?.url || '';
        if (!url.includes('/auth/me') && !url.includes('/auth/login') && !url.includes('/auth/register')) {
          localStorage.removeItem('pop_token');
          localStorage.removeItem('pop_user');
        }
      }
      return Promise.reject(error);
    }
  );
}

// Auth API (Requires backend - optional feature)
export const authAPI = {
  register: (data) => {
    if (!api) return Promise.reject(new Error('Backend not configured'));
    return api.post('/auth/register', data);
  },
  login: (data) => {
    if (!api) return Promise.reject(new Error('Backend not configured'));
    return api.post('/auth/login', data);
  },
  me: () => {
    if (!api) return Promise.reject(new Error('Backend not configured'));
    return api.get('/auth/me');
  },
};

// Products API (Backend preferred, Fallback to direct Swell)
export const productsAPI = {
  getAll: async (params = {}) => {
    // Try backend first (local dev with REACT_APP_BACKEND_URL set)
    if (api) {
      try {
        const response = await api.get('/products', { params });
        return { data: response.data };
      } catch (error) {
        console.error('Failed to fetch products from backend:', error);
      }
    }

    // Try Cloudflare Function (production - has drop filtering via REST API)
    if (!BACKEND_URL) {
      try {
        const queryParams = new URLSearchParams();
        if (params.brand) queryParams.set('brand', params.brand);
        if (params.featured !== undefined) queryParams.set('featured', params.featured);

        const url = `/api/products${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        const response = await fetch(url);

        if (response.ok) {
          const data = await response.json();
          // Transform to match expected format
          const results = data.map(transformProduct);
          return { data: results };
        }
      } catch (error) {
        console.error('Failed to fetch products from Cloudflare Function:', error);
      }
    }

    // Fallback to direct Swell (local dev without backend)
    const { brand, ...rest } = params;
    const swellParams = {
      limit: 100,
      expand: ['categories', 'purchase_options', 'images', 'variants'],
      ...rest
    };

    try {
      const response = await swell.products.list(swellParams);

      // Filter out drop products (products with drop_slug attribute)
      const filteredProducts = response.results.filter(p => {
        const attrs = p.attributes || {};
        // Exclude products that have drop_slug attribute
        return !attrs.drop_slug;
      });

      let results = filteredProducts.map(transformProduct);

      if (brand) {
        results = results.filter(p => p.brand === brand);
      }

      return { data: results };
    } catch (error) {
      console.error('Failed to fetch products from Swell:', error);
      return { data: [] };
    }
  },
  getOne: async (idOrSlug) => {
    if (api) {
      try {
        const response = await api.get(`/products/${idOrSlug}`);
        return { data: response.data };
      } catch (error) {
        console.error('Failed to fetch product from backend:', error);
      }
    }

    // Fallback to direct Swell
    let p = await swell.products.get(idOrSlug, {
      expand: ['categories', 'purchase_options', 'variants', 'images']
    });

    // In Swell-js, get() handles both ID and Slug
    if (p) {
      return { data: transformProduct(p) };
    }
    throw new Error("Product not found");
  },
  getCategories: async () => {
    if (api) {
      try {
        const response = await api.get('/categories');
        return { data: response.data };
      } catch (error) {
        console.error('Failed to fetch categories from backend:', error);
      }
    }

    try {
      const response = await swell.categories.list({ limit: 100 });
      return { data: response.results.map(c => c.name.toLowerCase()) };
    } catch (error) {
      console.error('Failed to fetch categories from Swell:', error);
      return { data: ["apparel", "accessories", "new arrivals"] }; // Fallback
    }
  },
  getBrands: () => Promise.resolve({ data: DEFAULT_BRANDS }),
  getBrand: (slug) => {
    const brand = DEFAULT_BRANDS.find(b => b.slug === slug) || DEFAULT_BRANDS[0];
    return Promise.resolve({ data: brand });
  },
  getFeatured: async () => {
    // Try backend first (local dev with REACT_APP_BACKEND_URL set)
    if (api) {
      try {
        const response = await api.get('/products', { params: { featured: true } });
        return { data: response.data.slice(0, 4) };
      } catch (error) {
        console.error('Failed to fetch featured products from backend:', error);
      }
    }

    // Try Cloudflare Function (production - has drop filtering via REST API)
    if (!BACKEND_URL) {
      try {
        const response = await fetch('/api/products?featured=true');
        if (response.ok) {
          const data = await response.json();
          const results = data.map(transformProduct);
          return { data: results.slice(0, 4) };
        }
      } catch (error) {
        console.error('Failed to fetch featured products from Cloudflare Function:', error);
      }
    }

    // Fallback to direct Swell (local dev without backend)
    try {
      const response = await swell.products.list({
        limit: 100,
        expand: ['categories', 'purchase_options', 'images', 'variants'],
        tags: 'featured'  // Filter for products tagged with "featured"
      });

      // Filter out drop products
      const filteredProducts = response.results.filter(p => {
        const attrs = p.attributes || {};
        return !attrs.drop_slug;
      });

      const results = filteredProducts.map(p => transformProduct(p, 'featured-swell'));
      // Return up to 4 featured products
      return { data: results.slice(0, 4) };
    } catch (error) {
      console.error('Failed to fetch featured products from Swell:', error);
      return { data: [] };
    }
  },
};

// Cart API (Backend preferred, Fallback to direct Swell)
export const cartAPI = {
  get: async () => {
    if (api) {
      try {
        const response = await api.get('/cart');
        return response.data;
      } catch (error) {
        console.error('Failed to fetch cart from backend:', error);
      }
    }

    const cart = await swell.cart.get({
      expand: ['items.product', 'items.product.categories']
    });
    if (cart && cart.items) {
      cart.items = await Promise.all(cart.items.map(transformCartItem));
    }
    return cart;
  },
  add: async (item) => {
    if (api) {
      try {
        const response = await api.post('/cart/add', item);
        return response.data.cart;
      } catch (error) {
        console.error('Failed to add to cart on backend:', error);
      }
    }

    const cart = await swell.cart.addItem(item);
    return cartAPI.get();
  },
  update: async (itemId, quantity) => {
    if (api) {
      try {
        const response = await api.put(`/cart/${itemId}`, { quantity });
        return response.data.cart;
      } catch (error) {
        console.error('Failed to update cart item on backend:', error);
      }
    }

    await swell.cart.updateItem(itemId, { quantity });
    return cartAPI.get();
  },
  remove: async (itemId) => {
    if (api) {
      try {
        const response = await api.delete(`/cart/${itemId}`);
        return response.data.cart;
      } catch (error) {
        console.error('Failed to remove cart item from backend:', error);
      }
    }

    await swell.cart.removeItem(itemId);
    return cartAPI.get();
  },
  clear: () => {
    if (api) {
      api.delete('/cart/clear').catch(console.error);
    }
    return swell.cart.setItems([]);
  },
  applyCoupon: async (code) => {
    await swell.cart.applyCoupon(code);
    // Refresh cart to get updated discount totals
    return cartAPI.get();
  },
  removeCoupon: async () => {
    await swell.cart.removeCoupon();
    // Refresh cart to get updated totals
    return cartAPI.get();
  },
  updateShippingAddress: async (address) => {
    await swell.cart.update({
      shipping: address
    });
    return cartAPI.get();
  },
  getShippingRates: async () => {
    return await swell.cart.getShippingRates();
  },
  selectShippingMethod: async (methodId) => {
    await swell.cart.update({
      shipping: {
        service: methodId
      }
    });
    return cartAPI.get();
  },
};

// Wishlist API (Backend preferred)
export const wishlistAPI = {
  get: async () => {
    if (api) {
      try {
        const response = await api.get('/wishlist');
        return response.data;
      } catch (error) {
        console.error('Failed to fetch wishlist:', error);
      }
    }
    return [];
  },
  add: async (product_id) => {
    if (api) {
      try {
        await api.post('/wishlist/add', { product_id });
        return;
      } catch (error) {
        console.error('Failed to add to wishlist:', error);
      }
    }
  },
  remove: async (product_id) => {
    if (api) {
      try {
        await api.delete(`/wishlist/${product_id}`);
        return;
      } catch (error) {
        console.error('Failed to remove from wishlist:', error);
      }
    }
  },
};

// Orders API (Backend preferred)
export const ordersAPI = {
  create: async (data) => {
    if (api) {
      try {
        const response = await api.post('/orders', data);
        return response.data;
      } catch (error) {
        console.error('Failed to create order:', error);
      }
    }
    return { id: 'mock-order' };
  },
  getAll: async () => {
    if (api) {
      try {
        const response = await api.get('/orders');
        return response.data;
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      }
    }
    return [];
  },
  getOne: async (id) => {
    if (api) {
      try {
        const response = await api.get(`/orders/${id}`);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch order:', error);
      }
    }
    return {};
  },
};

// Seed API (No longer needed)
export const seedAPI = {
  seed: () => Promise.resolve(),
};

// Stats API
export const statsAPI = {
  getStats: async () => {
    if (api) {
      try {
        const response = await api.get('/stats');
        return response.data;
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    }
    return { customers: 0, orders: 0, products: 0 };
  },
};

// Drop API
export const dropAPI = {
  getBySlug: async (slug) => {
    // Use backend endpoint which queries Swell REST API with custom attributes
    // Falls back to Cloudflare Function in production if BACKEND_URL not set
    try {
      const endpoint = BACKEND_URL ? `${API_BASE}/drop/${slug}` : `/api/drop/${slug}`;
      const response = await fetch(endpoint);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch drop' }));
        throw new Error(errorData.error || errorData.detail || 'Failed to fetch drop');
      }

      const dropData = await response.json();

      // Transform products to match our format
      const products = await Promise.all(
        dropData.products.map(async (p) => {
          // Fetch full product details from Swell SDK
          const fullProduct = await swell.products.get(p.id, {
            expand: ['variants', 'images']
          });
          return transformProduct(fullProduct);
        })
      );

      return {
        slug: dropData.slug,
        title: dropData.title,
        description: dropData.description,
        startAt: dropData.startAt,
        endAt: dropData.endAt,
        status: dropData.status,
        products
      };
    } catch (error) {
      console.error('Failed to fetch drop:', error);
      throw error;
    }
  }
};

export default api;
